import { getDotPath } from '@standard-schema/utils';
import { z } from 'zod';

import { highlighterApi } from '@highlight-cp/highlighter-api';
import { createLoggers } from '@highlight-cp/logger';

import { clientPortalSessionSchema } from './appSession';
import type { ClientPortalAppSession } from './appSession';

const { error: logError } = createLoggers({ tag: 'Client Portal session' });

/**
 * One signed-in Better Auth session on one page. Only projectPublicId reaches
 * the URL; the other fields give each session its own cache entry, so a new
 * sign-in always asks again.
 */
interface ClientPortalSessionArg {
  sessionId: string | null;
  userId: string | null;
  email: string | null;
  role: string | null;
  projectPublicId?: string;
}

export type ClientPortalSessionAnswer =
  | { status: 'authenticated'; session: ClientPortalAppSession }
  | { status: 'unauthenticated' }
  | { status: 'unauthorized' };

const answerSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('authenticated'), session: clientPortalSessionSchema }),
  z.object({ status: z.literal('unauthenticated') }),
  z.object({ status: z.literal('unauthorized') }),
]);

// 401 and 403 are answers about the person, not failures.
const answerForStatus: Partial<Record<number, ClientPortalSessionAnswer>> = {
  401: { status: 'unauthenticated' },
  403: { status: 'unauthorized' },
};

// RTK keeps one API slice per base URL, and highlighterApi already owns /api/.
// injectEndpoints returns that same slice, typed with this endpoint.
export const clientPortalSessionApi = highlighterApi.injectEndpoints({
  // Hot reload runs this again; without it RTK keeps the first definition.
  overrideExisting: true,
  endpoints: (build) => ({
    getClientPortalSession: build.query<ClientPortalSessionAnswer, ClientPortalSessionArg>({
      // Each call also points the staff scope cookie at its project, so an answer
      // kept for a project the tab left would leave the cookie on the other one.
      keepUnusedDataFor: 0,
      query: ({ projectPublicId }) => ({
        url: 'auth/client-portal-session',
        params: projectPublicId ? { projectPublicId } : undefined,
        validateStatus: (response) => response.ok || response.status in answerForStatus,
      }),
      transformResponse: (body: unknown, meta): ClientPortalSessionAnswer => {
        const answer = meta?.response && answerForStatus[meta.response.status];
        if (answer) {
          return answer;
        }

        // responseSchema checks this body next.
        return { status: 'authenticated', session: body as ClientPortalAppSession };
      },
      responseSchema: answerSchema,
      onSchemaFailure: (failure, { endpoint }) => {
        // The failure carries the parsed body, which holds the email.
        logError('Client Portal session response failed its schema', {
          endpoint,
          paths: failure.issues.map(getDotPath),
        });
      },
      catchSchemaFailure: () => ({
        status: 'CUSTOM_ERROR',
        error: 'Invalid Client Portal session response',
      }),
    }),
  }),
});

export const { useGetClientPortalSessionQuery } = clientPortalSessionApi;
